import { useTranslation } from "react-i18next";
import { HiOutlineCalendar } from "react-icons/hi";
import { HiOutlineArrowPath, HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { useYearRangeDates } from "hooks/useYearRangeDates";

interface DateRangeFilterProps {
  fromDate: string;
  toDate: string;
  onFromYearChange: (year: string) => void;
  onToYearChange: (year: string) => void;
  onApply?: () => void;
  className?: string;
  sectors?: Array<{ id: number; name: string; key: string }>;
  selectedSector?: string | number | null;
  onSectorChange?: (sectorId: string | null) => void;
  showSectorFilter?: boolean;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  fromDate,
  toDate,
  onFromYearChange,
  onToYearChange,
  onApply,
  className = "",
  sectors = [],
  selectedSector = null,
  onSectorChange,
  showSectorFilter = false,
}) => {
  const { t } = useTranslation();
  const {
    currentYear,
    years,
    getYearStartISO,
    getYearEndISO,
    extractYearFromISO,
  } = useYearRangeDates();

  const currentFromYear = fromDate ? extractYearFromISO(fromDate) : currentYear;
  const currentToYear = toDate ? extractYearFromISO(toDate) : currentYear;

  const handleFromYearChange = (year: number) => {
    onFromYearChange(getYearStartISO(year));
    if (year > currentToYear) {
      onToYearChange(getYearEndISO(year));
    }
  };

  const handleToYearChange = (year: number) => {
    if (year < currentFromYear) {
      onToYearChange(getYearEndISO(currentFromYear));
    } else {
      onToYearChange(getYearEndISO(year));
    }
  };

  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <div className="flex items-center gap-2">
        <HiOutlineCalendar className="h-5 w-5 text-gray-400" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t("dashboard.charts.from")}:
        </span>
        <select
          value={currentFromYear}
          onChange={(e) => handleFromYearChange(Number(e.target.value))}
          className="rounded-xl border-2 border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-900 transition-all duration-200 hover:border-indigo-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t("dashboard.charts.to")}:
        </span>
        <select
          value={currentToYear}
          onChange={(e) => handleToYearChange(Number(e.target.value))}
          className="rounded-xl border-2 border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-900 transition-all duration-200 hover:border-indigo-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      {showSectorFilter && sectors.length > 0 && onSectorChange && (
        <div className="flex items-center gap-2">
          <HiOutlineBuildingOffice2 className="h-5 w-5 text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("dashboard.charts.sector")}:
          </span>
          <select
            value={selectedSector || "all"}
            onChange={(e) => {
              const value = e.target.value;
              onSectorChange(value === "all" ? null : value);
            }}
            className="w-full rounded-xl border-2 border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-900 transition-all duration-200 hover:border-indigo-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            <option value="all">{t("dashboard.charts.allSectors")}</option>
            {sectors.map((sector) => (
              <option key={sector.id} value={sector.id}>
                {t(`industrySectors.${sector.key}`)}
              </option>
            ))}
          </select>
        </div>
      )}

      {onApply && (
        <button
          onClick={onApply}
          className="flex items-center rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-1.5 text-sm font-medium text-white shadow-lg shadow-indigo-500/25 transition-all duration-200 hover:shadow-xl hover:shadow-indigo-500/30"
        >
          <HiOutlineArrowPath className="me-1.5 inline-block h-4 w-4" />
          {t("buttons.apply")}
        </button>
      )}
    </div>
  );
};

export default DateRangeFilter;
